/** Ustawienia EBI firmy (urzędu) — BE EbiOfficeSettingsDto. */
export interface IEbiOfficeSettings {
  IsEnabled: boolean;
  /** Urząd jest połączony z platformą EBI — bez tego pozostałe ustawienia EBI są zablokowane. */
  IsConnected: boolean;
  /** Fragment adresu portalu na platformie EBI: `{PortalUrl platformy}/{Slug}`. */
  Slug?: string | null;
  OfficeName?: string | null;
  PortalHeader?: string | null;
  /** Pełny adres portalu dla petentów na platformie EBI (gdy jest połączenie i slug). */
  PortalUrl?: string | null;
  /** Nazwa urzędu z Ustawienia → Urząd — używana w portalu i awizo, gdy `OfficeName` jest puste. */
  DefaultOfficeName?: string | null;
  /** Administrator albo właściciel firmy. */
  CanEdit: boolean;
}

/**
 * Połączenie Magistratu z platformą EBI (portal petenta hostowany poza urzędem): synchronizacja pism wychodzących do
 * EBI i wniosków złożonych w EBI — BE EbiOfficeConnectionDto (`api/ebi/office/connection`).
 */
export interface IEbiOfficeConnection {
  IsConnected: boolean;
  /** Adres platformy EBI jest ustawiony w konfiguracji serwera Magistratu (`Ebi:PlatformUrl`) — bez niego nie da się połączyć. */
  IsPlatformConfigured: boolean;
  /** Adres API platformy EBI z konfiguracji serwera Magistratu (`Ebi:PlatformUrl`). */
  ApiUrl?: string | null;
  /** Identyfikator urzędu na platformie EBI. */
  OfficeId?: string | null;
  /** Identyfikator klienta API urzędu na platformie EBI. */
  ClientId?: string | null;
  Slug?: string | null;
  /** Pełny adres portalu urzędu dla petentów. */
  PortalUrl?: string | null;
  ConnectedDate?: string | null;
  LastSyncDate?: string | null;
  /** Błąd ostatniej synchronizacji; `null` — ostatnia się udała. */
  LastSyncError?: string | null;
  /** Synchronizacja zlecona („Synchronizuj teraz”, połączenie), jeszcze niewykonana przez usługę w tle Magistratu. */
  SyncRequested?: boolean;
  /** Tylko w odpowiedzi na rozłączenie: platforma EBI nie potwierdziła wyłączenia klienta (rozłączenie lokalne się wykonało). */
  Warning?: string | null;
  /** Administrator albo właściciel firmy. */
  CanEdit: boolean;
}

/** Połączenie z platformą EBI kodem rejestracyjnym od operatora platformy — BE EbiConnectRequest (adres platformy z konfiguracji serwera). */
export interface IEbiOfficeConnectRequest {
  EnrollmentCode: string;
}

/** Szablon maila EBI (Ustawienia → Biuro Interesanta → Powiadomienia) — BE EbiMailTemplateDto. */
export interface IEbiMailTemplate {
  /** Nazwa typu maila w BE (np. `EbiParcelNotice`). */
  Key: string;
  Name: string;
  Description: string;
  /** `petent` albo `urząd`. */
  Recipient: string;
}

/** Podgląd szablonu z danymi urzędu (herb wklejony jako data:) — BE EbiMailPreviewDto. */
export interface IEbiMailPreview {
  Key: string;
  Name: string;
  Recipient: string;
  Subject: string;
  Preheader: string;
  Html: string;
  HasEmblem: boolean;
}

/** Kolory maili EBI (#RRGGBB): tło wiadomości, wypełnienie przycisku, tło stopki — BE EbiMailThemeDto. */
export interface IEbiMailTheme {
  Background: string;
  ButtonColor: string;
  FooterBackground: string;
}

/** Gotowy wariant kolorystyki — BE EbiMailThemePresetDto. */
export interface IEbiMailThemePreset extends IEbiMailTheme {
  Key: string;
  Name: string;
}

/** Kolorystyka maili firmy (Powiadomienia) — BE EbiMailThemeSettingsDto. */
export interface IEbiMailThemeSettings {
  Theme: IEbiMailTheme;
  /** Klucz wariantu o dokładnie tych kolorach; `null` = własne kolory. */
  PresetKey?: string | null;
  IsDefault: boolean;
  Presets: IEbiMailThemePreset[];
  /** Firma ma zapisane ustawienia EBI — bez nich kolorów nie da się zapisać (są ich częścią). */
  HasSettings: boolean;
  /** Administrator albo właściciel firmy. */
  CanEdit: boolean;
}

/** Zapis kolorystyki (wszystkie puste = domyślne) — BE EbiMailThemeRequest. */
export interface IEbiMailThemeRequest {
  Background?: string | null;
  ButtonColor?: string | null;
  FooterBackground?: string | null;
}

/** Kolory portalu petenta (#RRGGBB): dwie plamy tła i akcent — BE EbiPortalThemeDto. */
export interface IEbiPortalTheme {
  Background1: string;
  Background2: string;
  Accent: string;
}

/** Gotowy wariant wyglądu portalu — BE EbiPortalThemePresetDto. */
export interface IEbiPortalThemePreset extends IEbiPortalTheme {
  Key: string;
  Name: string;
}

/** Wygląd portalu firmy (zakładka Wygląd) — BE EbiPortalThemeSettingsDto. */
export interface IEbiPortalThemeSettings {
  Theme: IEbiPortalTheme;
  /** Klucz wariantu o dokładnie tych kolorach; `null` = własne kolory. */
  PresetKey?: string | null;
  IsDefault: boolean;
  Presets: IEbiPortalThemePreset[];
  /** Firma ma zapisane ustawienia EBI — bez nich kolorów nie da się zapisać (są ich częścią). */
  HasSettings: boolean;
  /** Administrator albo właściciel firmy. */
  CanEdit: boolean;
}

/** Zapis wyglądu portalu (wszystkie puste = domyślne) — BE EbiPortalThemeRequest. */
export interface IEbiPortalThemeRequest {
  Background1?: string | null;
  Background2?: string | null;
  Accent?: string | null;
}

/** Wnioski przyjmowane przez urząd (zakładka Wnioski) — BE EbiSubmissionFormsSettingsDto. */
export interface IEbiSubmissionFormsSettings {
  /** Kody włączonych wniosków (zapisany wybór albo domyślny). */
  Codes: string[];
  /** Obowiązuje wybór domyślny (wnioski gminne). */
  IsDefault: boolean;
  /** Firma ma zapisane ustawienia EBI — bez nich wyboru nie da się zapisać. */
  HasSettings: boolean;
  /** Administrator albo właściciel firmy. */
  CanEdit: boolean;
  /** Obsługa wniosków (pracownik, kod JRWA) — tylko wnioski, którym coś przypisano. */
  Handling: IEbiSubmissionFormHandling[];
}

/**
 * Obsługa rodzaju wniosku w urzędzie — BE EbiSubmissionFormHandlingDto. Pracownik dostaje złożony wniosek z pominięciem
 * biura podawczego (dekret po rejestracji), a kod JRWA trafia do przesyłki i jej metryki.
 */
export interface IEbiSubmissionFormHandling {
  Code: string;
  /** Pracownik obsługujący wniosek; `null` = biuro podawcze. */
  HandlerUserId: string | null;
  /** Pozycja wykazu JRWA firmy. */
  ClassificationId: string | null;
}

/** Zapis wyboru wniosków; `Codes: null` = wybór domyślny — BE EbiSubmissionFormsRequest. */
export interface IEbiSubmissionFormsRequest {
  Codes: string[] | null;
  /** Pełna lista obsługi wniosków (zastępuje zapisaną); `null` = bez zmian. */
  Handling?: IEbiSubmissionFormHandling[] | null;
}

export interface IEbiOfficeSettingsRequest {
  IsEnabled: boolean;
  Slug: string;
  OfficeName?: string | null;
  PortalHeader?: string | null;
}

/** Bieżący klucz petenta dla adresata EBI (podmiot z książki adresowej). */
export interface IEbiRecipientKey {
  ParticipantId: number;
  /** Base64; `null` — petent nie ma jeszcze klucza, przesyłka poczeka na przeszyfrowanie. */
  PublicKey?: string | null;
  KeyIdHex?: string | null;
}

// ── Etap 4: przeszyfrowanie (KeysManager) — BE EbiKeyRequest*Dto ──────────

export interface IEbiKeyShareSource {
  /** Base64 wiadomości PGP z kluczem prywatnym obiektu. */
  PrivateKey: string;
  /** Klucz nadrzędny (teczki), gdy szyfrogram jest nim zawinięty. */
  ParentKeyId?: string | null;
  ParentPrivateKey?: string | null;
}

export interface IEbiKeyShareItem {
  /** Klucz obiektu adresata EBI, który BE nadpisze wynikiem. */
  ObjectKeyId: string;
  /** Klucz publiczny obiektu (base64) — weryfikacja, że odszyfrowano właściwy klucz. */
  ObjectPublicKey?: string | null;
  /** Szyfrogramy tego samego klucza obiektu; wystarczy odszyfrować jeden. */
  Sources: IEbiKeyShareSource[];
  /** Podmiot doręczenia — musi się zgadzać z oświadczeniem nadawcy. */
  ParticipantId?: number | null;
  /** Oświadczenie nadawcy (base64 UTF-8 JSON) i podpis kluczem firmy (base64) — decyzja D1, utils/ebiKeyBinding. */
  KeyBinding?: string | null;
  KeyBindingSignature?: string | null;
}

export interface IEbiKeyRequestClaim {
  RequestId: string;
  LeaseUntil: string;
  /** Bieżący klucz petenta (base64 binarnego packetlistu). */
  PetentPublicKey: string;
  PetentKeyIdHex: string;
  /** Aktywna generacja klucza firmy (podklucz szyfrujący, hex) — BE wymaga jej w wyniku. */
  CompanyKeyIdHex: string;
  Items: IEbiKeyShareItem[];
}

export interface IEbiKeyShareResult {
  ObjectKeyId: string;
  /** Base64 wiadomości PGP zaadresowanej dokładnie do [klucz firmy, klucz petenta]. */
  PrivateKey: string;
}

export interface IEbiKeyRequestCompleteResult {
  SharedCount: number;
  AccountId: string;
  BecameAvailable: boolean;
}

export interface IEbiKeyRequestsSummary {
  Pending: number;
  InProgress: number;
  Failed: number;
  OldestRequestedDate?: string | null;
  /** Czy zalogowany obsługuje kolejkę (klucz firmy + dostęp do przesyłek całej firmy). */
  CanProcess: boolean;
  /** Aktywna generacja klucza firmy (podklucz szyfrujący, hex) — karta z inną generacją nie przejmuje zadań. */
  CompanyKeyIdHex?: string | null;
}

// ── Usługa przeszyfrowań na serwerze urzędu (ebi-agent) — BE EbiAgent*Dto ──

export enum EEbiAgentStatus {
  /** Utworzona, czeka na rejestrację jednorazowym kodem. */
  Enrolling = 0,
  /** Zarejestrowana; z grantem klucza firmy obsługuje kolejkę. */
  Active = 1,
  Revoked = 9,
}

export interface IEbiAgent {
  Id: string;
  Name: string;
  Host?: string | null;
  Status: EEbiAgentStatus;
  CreationDate: string;
  EnrollTokenExpires?: string | null;
  EnrolledDate?: string | null;
  LastSeenDate?: string | null;
  LastSeenIp?: string | null;
  /** Klucz publiczny usługi (base64) — nim przeglądarka zawija klucz prywatny firmy. */
  PublicKey?: string | null;
  KeyIdHex?: string | null;
  HasKey: boolean;
  /** Grant dotyczy aktywnej generacji klucza firmy. */
  KeyIsCurrent: boolean;
  GrantedDate?: string | null;
  GrantedByUserId?: string | null;
  GrantedBy?: string | null;
  /** Usługa może obsługiwać kolejkę (aktualny grant, wydający nadal ma klucz i dostęp). */
  CanProcess: boolean;
  LastError?: string | null;
  LastErrorDate?: string | null;
  SharedCount: number;
  RevokedDate?: string | null;
}

/** Odpowiedź na utworzenie usługi — kod rejestracji widoczny tylko tutaj. */
export interface IEbiAgentCreated {
  Agent: IEbiAgent;
  EnrollToken: string;
  EnrollTokenExpires: string;
}

export interface IEbiAgentGrantRequest {
  WorkspaceKeyId: string;
  /** Base64 wiadomości PGP zaadresowanej dokładnie do klucza usługi, z kluczem prywatnym firmy w środku. */
  CompanyKeyGrant: string;
}

// ── Etap 6: rejestr EBI ──────────────────────────────────────────────────

/** Stan doręczenia EBI — BE EbiDeliveryStatus. */
export enum EEbiDeliveryStatus {
  Prepared = 0,
  WaitingForKey = 1,
  Available = 2,
  Received = 3,
  DeemedDelivered = 4,
  Cancelled = 9,
}

export interface IEbiRegisterRequest {
  /** Daty kalendarzowe (yyyy-MM-dd, czas polski). */
  From?: string | null;
  To?: string | null;
  Text?: string | null;
  Status?: EEbiDeliveryStatus | null;
  WaitingForShare?: boolean;
  Skip: number;
  /** 0 = wszystkie (wydruk), z limitem po stronie BE. */
  Take: number;
}

export interface IEbiRegisterItem {
  DeliveryId: string;
  OutgoingParcelId: string;
  Signature?: string | null;
  Title?: string | null;
  RecipientName?: string | null;
  Email: string;
  Status: EEbiDeliveryStatus;
  /** Petent ma klucz, ale przesyłka nie jest jeszcze dla niego udostępniona. */
  WaitingForShare: boolean;
  SendDate?: string | null;
  NoticeDate?: string | null;
  SecondNoticeDate?: string | null;
  AvailableDate?: string | null;
  ReceivedDate?: string | null;
  OpenedDate?: string | null;
  DeemedDeliveredDate?: string | null;
  DeemedDeliveryDue?: string | null;
  Author?: string | null;
  CaseId?: string | null;
  CaseSignature?: string | null;
  LastError?: string | null;
}

export interface IEbiRegisterResponse {
  Items: IEbiRegisterItem[];
  Total: number;
  /** Zalogowany widzi przesyłki całej firmy (inaczej tylko własne). */
  IsTeamView: boolean;
}
